import React, { ComponentProps, useEffect, useMemo, useState } from 'react';
import Workspace from '../../components/Workspace/Workspace';
import { PurchaseRequirementDetailPreview } from '../../graphql/entities';
import RaisePurchaseOrderFromShortagePreview from '../../graphql/mutations/purchases/RaisePurchaseOrderFromShortagePreview.graphql';
import DeallocateFromPreviewsMutation from 'graphql/mutations/purchases/DeallocateFromPreviewsMutation.graphql';
import BreakdownPurchaseRequirementDetailPreviewByComponents from '../../graphql/mutations/purchases/BreakdownPurchaseRequirementDetailPreviewByComponents.graphql';
import { Tooltip } from '@fluentui/react-components';
import { Toggle } from '@fluentui/react/lib/Toggle';
import { useId } from '@fluentui/react-hooks';
import Row from '../../components/connectedForm/Row';
import Fieldset from '../../components/connectedForm/Fieldset';
import {
  BasicWorkspaceContextAction,
  useBasicWorkspaceAction,
  useWorkspaceAction,
  WorkspaceActionButtons,
} from '../../components/Workspace/WorkspaceActionButtons';
import { ViewContextProvider } from '../../components/Context/ViewContext';
import RaiseShortagesFromPreviewsMutation from '../../graphql/mutations/purchases/ShortagesFromPreviewsMutation.graphql';
import RaiseAssemblyShortagesFromPreviewsMutation from '../../graphql/mutations/purchases/AssemblyShortagesFromPreviewsMutation.graphql';
import RaiseAssembliesFromPreviewsMutation from '../../graphql/mutations/purchases/AssembliesFromPreviewsMutation.graphql';
import { useWorkspace, WorkspaceConfig } from '../../graphql/hooks/useWorkspace';
import {
  Direction,
  FilterOperation,
  IFilterInput,
  IPurchaseRequirementDetailPreview,
  IRaiseShortagesFromPreviewsMutationResult,
  IRaiseShortagesFromPreviewsMutationVariables,
  Maybe,
  ProductClass,
} from '../../graphql/schema/p4types';
import { Paths } from '../../@types/keyOfNested';
import { IWorkspaceColumnOverride, IWorkspaceProps } from '../../@types/workspaceTypes';
import { useActionCriteria, useActionProps } from '../../utils/useActionProps';
import getClientMutationId from '../../utils/getClientMutationId';
import { useRaisePOActions } from '../../actions/purchasing/PurchaseRequirementDetailWorkspaceAction';
import { useSalesOrderConfig } from '../../utils/FetchConfig';
import { add } from 'date-fns';
import normalizeVariables from '../../graphql/schema/normalizeVariables';

const { type, workspace, title } = PurchaseRequirementDetailPreview;

const PurchaseRequirementDetailPreviewAssemblyActions = (props: ComponentProps<typeof WorkspaceActionButtons>) => {
  const addToAssemblyShortagesAction = useBasicWorkspaceAction({
    mutation: RaiseAssemblyShortagesFromPreviewsMutation,
    type: 'purchaseRequirementDetailPreviews',
    text: 'Add to Assembly Shortages (Short Quantity)',
    actionReference: 'PURCHASING_ORDERS_CREATE',
  });

  const raiseAssemblyShortagesAction = useBasicWorkspaceAction({
    mutation: RaiseAssembliesFromPreviewsMutation,
    type: 'purchaseRequirementDetailPreviews',
    text: 'Raise Assemblies (Short Quantity)',
    actionReference: 'PURCHASING_ORDERS_CREATE',
  });

  const { actionButtons: raisePOActions, modals } = useRaisePOActions({
    raiseMutation: RaisePurchaseOrderFromShortagePreview,
    isPreview: true,
  });

  const addToAssembliesShortagesFullQtyAction = useAddToAssembliesShortagesFullQtyAction();
  const raiseAssemblyFullQtyAction = useAddToAssembliesFullQtyAction();

  const actionButtons = [
    addToAssemblyShortagesAction,
    addToAssembliesShortagesFullQtyAction,
    raiseAssemblyShortagesAction,
    raiseAssemblyFullQtyAction,
    ...raisePOActions,
  ];

  const viewContextValue = useMemo(() => ({ uniqueName: 'PurchaseRequirementDetailPreviewWorkspaceActions' }), []);

  //View Context wrapper is to 'hide' the workspace view context from the form. Otherwise we get some unintentional side effects (e.g. Shimmer rows on the item grid).
  return (
    <>
      <ViewContextProvider value={viewContextValue}>{modals}</ViewContextProvider>
      <WorkspaceActionButtons {...props} actionButtons={actionButtons} />
    </>
  );
};

const PurchaseRequirementDetailPreviewActions = (props: ComponentProps<typeof WorkspaceActionButtons>) => {
  const addToShortagesAction = useBasicWorkspaceAction({
    mutation: RaiseShortagesFromPreviewsMutation,
    type: 'purchaseRequirementDetailPreviews',
    text: 'Add to Shortages (Short Quantity)',
    actionReference: 'PURCHASING_ORDERS_CREATE',
  });

  const deallocateFromPreviewsAction = useBasicWorkspaceAction({
    mutation: DeallocateFromPreviewsMutation,
    type: 'purchaseRequirementDetailPreviews',
    text: 'Deallocate Lines',
    actionReference: 'SALES_PRODUCT_DEALLOCATION',
  });

  const breakdownComponentShortagesAction = useBasicWorkspaceAction({
    mutation: BreakdownPurchaseRequirementDetailPreviewByComponents,
    errorMessage: 'Could not break down component shortages',
    text: 'Breakdown Component Shortages',
    type: 'potentials',
    actionReference: 'PURCHASING_ORDERS_CREATE',
  });

  const { actionButtons: raisePOActions, modals } = useRaisePOActions({
    raiseMutation: RaisePurchaseOrderFromShortagePreview,
    isPreview: true,
  });

  const addToShortagesFullQtyAction = useAddToShortagesFullQtyAction();

  const actionButtons = [
    addToShortagesAction,
    addToShortagesFullQtyAction,
    deallocateFromPreviewsAction,
    breakdownComponentShortagesAction,
    ...raisePOActions,
  ];

  const viewContextValue = useMemo(() => ({ uniqueName: 'PurchaseRequirementDetailPreviewWorkspaceActions' }), []);

  //View Context wrapper is to 'hide' the workspace view context from the form. Otherwise we get some unintentional side effects (e.g. Shimmer rows on the item grid).
  return (
    <>
      <ViewContextProvider value={viewContextValue}>{modals}</ViewContextProvider>
      <WorkspaceActionButtons {...props} actionButtons={actionButtons} />
    </>
  );
};

const PURCHASE_REQUIREMENT_DETAIL_PREVIEW_FIELD_NAMES: Array<Paths<IPurchaseRequirementDetailPreview, 3>> = [
  'product.reference',
  'mainSupplierDetails.supplier.name',
  'product.description',
  'warehouse.description',
  'product.productClass',
  'shortageOrigin',
  'requiredDate',
  'requiredQuantity',
  'leadTime',
  'lineValue',
  'orderQuantityMinimum',
  'onOrder',
];

const PURCHASE_REQUIREMENT_DETAIL_PREVIEW_COLUMN_OVERRIDES: IWorkspaceColumnOverride = {
  'mainSupplierDetails.orderQuantityIncrement': {
    name: 'Main Supplier - Order Quantity Increment',
  },
  'mainSupplierDetails.supplier.name': {
    name: 'Main Supplier - Name',
  },
  averageMonthlyUsage: {
    sortable: false,
    tooltip:
      'Dynamic column - Usage generated live, which may slow down loading of this workspace. Sorting, filtering, and dynamic reporting is unavailable due to dynamic generation of value',
    styles: { headerCell: { backgroundColor: '#d0edff' } },
  },
};

const useAddToShortagesFullQtyAction = () => {
  const actionCriteria = useActionCriteria();
  const variables: IRaiseShortagesFromPreviewsMutationVariables = {
    input: {
      clientMutationId: getClientMutationId(),
      filters: actionCriteria.filters,
      purchaseRequirementDetailPreviews: actionCriteria.actionEntities,
      include: actionCriteria.include,
      fullQuantity: true,
    },
  };
  const onClick = useWorkspaceAction<
    IRaiseShortagesFromPreviewsMutationResult,
    IRaiseShortagesFromPreviewsMutationVariables
  >({ mutation: RaiseShortagesFromPreviewsMutation, variables });
  return {
    key: `toShortagesFullQtyAction`,
    text: 'Add to Shortages (Full Quantity)',
    onClick: onClick,
    actionReference: 'PURCHASING_ORDERS_CREATE',
  };
};

const useAddToAssembliesShortagesFullQtyAction = () => {
  const actionProps = useActionProps('purchaseRequirementDetailPreviews');
  const payload = normalizeVariables(actionProps);
  payload.purchaseRequirementDetailPreviews = payload.purchaseRequirementDetailPreviews.map(
    (requirement: IPurchaseRequirementDetailPreview) => {
      return { ...requirement, requiredQuantity: requirement.salesOrderLine?.outstanding };
    }
  );
  payload.useModifiedRequirements = true;
  const variables = { input: { clientMutationId: getClientMutationId(), ...payload } };
  const onClick = useWorkspaceAction({ mutation: RaiseAssemblyShortagesFromPreviewsMutation, variables });
  return {
    key: `toAssembliesShortagesQtyAction`,
    text: 'Add to Assembly Shortages (Full Quantity)',
    onClick: onClick,
    actionReference: 'PURCHASING_ORDERS_CREATE',
  };
};

const useAddToAssembliesFullQtyAction = () => {
  const actionProps = useActionProps('purchaseRequirementDetailPreviews');
  const payload = normalizeVariables(actionProps);
  payload.purchaseRequirementDetailPreviews = payload.purchaseRequirementDetailPreviews.map(
    (requirement: IPurchaseRequirementDetailPreview) => {
      return { ...requirement, requiredQuantity: requirement.salesOrderLine?.outstanding };
    }
  );
  payload.useModifiedRequirements = true;
  const variables = { input: { clientMutationId: getClientMutationId(), ...payload } };
  const onClick = useWorkspaceAction({ mutation: RaiseAssembliesFromPreviewsMutation, variables });
  return {
    key: `toAssembliesFullQtyAction`,
    text: 'Raise Assemblies (Full Quantity)',
    onClick: onClick,
    actionReference: 'PURCHASING_ORDERS_CREATE',
  };
};

type IPurchaseRequirementDetailPreviewWorkspaceProps = Omit<
  IWorkspaceProps<IPurchaseRequirementDetailPreview>,
  'entity'
> & {
  salesOrderNumber?: number;
  customerName?: string;
  assemblyOptions?: boolean;
  scheduledSalesOrder?: boolean;
};

const PurchaseRequirementDetailPreviewWorkspace = (props: IPurchaseRequirementDetailPreviewWorkspaceProps) => {
  const zeroFilterTooltipId = useId('zeroFilterTooltipId');
  const scheduledLinesTooltipId = useId('scheduledLinesTooltipId');
  const [includeZeroFilter, setIncludeZeroFilter] = useState(true);
  const [includeAllScheduledLines, setIncludeAllScheduledLines] = useState(true);
  const orderConfig = useSalesOrderConfig();

  const allocationCutOfDate = useMemo(() => {
    let cutOffDate: Date | null = null;
    if (orderConfig?.allocationDaysAhead) {
      const daysAhead = orderConfig?.allocationDaysAhead + 1;
      const today = new Date();
      cutOffDate = add(today, { days: daysAhead });
    }
    return cutOffDate;
  }, [orderConfig?.allocationDaysAhead]);

  const {
    salesOrderNumber,
    customerName,
    assemblyOptions,
    scheduledSalesOrder,
    onClose,
    predefinedFilters,
    ...workspacePassThroughProps
  } = props;

  const filters: Array<IFilterInput> = useMemo(() => {
    const filterList: Array<IFilterInput> = [];

    const includeZeroFilterFilter = {
      filterOperation: FilterOperation.GreaterThan,
      filterParameters: {
        field: 'requiredQuantity',
        value: '0',
      },
    };

    if (includeZeroFilter) {
      filterList.push(includeZeroFilterFilter);
    }

    const scheduledOrdersFilter = {
      filterOperation: FilterOperation.LessThan,
      filterParameters: {
        field: 'salesOrderLine.targetDate',
        value: allocationCutOfDate?.toISOString().split('T')[0],
      },
    };

    if (scheduledSalesOrder && includeAllScheduledLines && allocationCutOfDate) {
      filterList.push(scheduledOrdersFilter);
    }

    if (predefinedFilters) {
      filterList.push(...predefinedFilters);
    }

    if (assemblyOptions) {
      filterList.push({
        filterOperation: FilterOperation.Equals,
        filterParameters: {
          field: 'product.productClass',
          value: ProductClass.Assembly,
        },
      });
    } else {
      filterList.push({
        filterOperation: FilterOperation.NotEquals,
        filterParameters: {
          field: 'product.productClass',
          value: ProductClass.Assembly,
        },
      });
    }

    return filterList;
  }, [includeZeroFilter, allocationCutOfDate, includeAllScheduledLines, predefinedFilters, assemblyOptions]);

  const workspaceProps = useWorkspace<IPurchaseRequirementDetailPreview>(
    props as WorkspaceConfig<IPurchaseRequirementDetailPreview>,
    PurchaseRequirementDetailPreview,
    {
      columns: PURCHASE_REQUIREMENT_DETAIL_PREVIEW_FIELD_NAMES as Array<Paths<IPurchaseRequirementDetailPreview>>,
      columnOverrides: PURCHASE_REQUIREMENT_DETAIL_PREVIEW_COLUMN_OVERRIDES,
      filters: filters,
      initialSortColumn: 'updatedAt',
      initialSortDirection: Direction.Desc,
    }
  );

  useEffect(() => {
    const edges = workspaceProps.listData?.edges;
    if (edges && edges.length === 0 && onClose) {
      onClose();
    }
  }, [props, workspaceProps.listData?.edges]);

  const renderContextActionButtons = ({ rowData }: { rowData: IPurchaseRequirementDetailPreview }) => {
    return [
      <BasicWorkspaceContextAction
        mutation={BreakdownPurchaseRequirementDetailPreviewByComponents}
        errorMessage={'Could not break down component shortages'}
        title={'Reviewing Component Shortages'}
        label={'Breakdown Component Shortages'}
        type={'potentials'}
        rowData={rowData}
      />,
    ];
  };

  const renderZeroFilterToggle = useMemo(() => {
    return (
      <Fieldset cols={1 / 4}>
        <Tooltip
          content="Choose whether lines with no requirement quantity should be included in the preview."
          relationship="label"
        >
          <Toggle
            defaultChecked={false}
            onText="Including items currently in stock / on order"
            offText="Excluding items currently in stock / on order"
            onChange={(event, checked) => {
              setIncludeZeroFilter(!checked);
            }}
            aria-describedby={zeroFilterTooltipId}
          />
        </Tooltip>
      </Fieldset>
    );
  }, []);

  const renderScheduledOrderLineToggle = useMemo(() => {
    if (!scheduledSalesOrder) {
      return null;
    }

    return (
      <Fieldset cols={1 / 4}>
        <Tooltip
          content="Choose whether lines scheduled beyond the allocation days limit should be included in the preview."
          relationship="label"
        >
          <Toggle
            defaultChecked={false}
            onText="Including all scheduled lines"
            offText="Excluding scheduled lines beyond allocation date"
            onChange={(event, checked) => {
              setIncludeAllScheduledLines(!checked);
            }}
            aria-describedby={scheduledLinesTooltipId}
          />
        </Tooltip>
      </Fieldset>
    );
  }, [scheduledLinesTooltipId, scheduledSalesOrder]);

  return (
    <Workspace<IPurchaseRequirementDetailPreview>
      entityType={type}
      title={`${title} ${assemblyOptions ? '(Assemblies)' : ''} ${salesOrderNumber} ${customerName}`}
      defaultQuery={workspace}
      entity={PurchaseRequirementDetailPreview}
      {...workspaceProps}
      {...workspacePassThroughProps}
      permittedOperations={0}
      enableRowSelect={true}
      renderActionButtons={renderContextActionButtons}
      customHeader={
        <Row style={{ marginLeft: 0 }}>
          {renderZeroFilterToggle}
          {renderScheduledOrderLineToggle}
        </Row>
      }
      workspaceActionButtonsHOC={
        assemblyOptions ? PurchaseRequirementDetailPreviewAssemblyActions : PurchaseRequirementDetailPreviewActions
      }
    />
  );
};

export default PurchaseRequirementDetailPreviewWorkspace;
